import React, { useCallback, useEffect, useRef, useState } from 'react';

import { usePermissions } from '../../context/PermissionContext';
import { BUSY_FILES_PERMISSIONS } from '../../constants/reports';
import { ROUTES } from '../../constants/routes';
import {
  BUSY_READ_ONLY_SHEETS,
  BUSY_SERIES,
  BUSY_SERIES_RULE,
  BUSY_SHEETS,
  BUSY_TRADEMARK_NOTE,
  FILE_NOT_FOUND_REFRESHED_TEXT,
  FILE_NOT_FOUND_RETRY_TEXT,
  FILE_RECHECKING_TEXT,
  MONTHLY_FILES_EMPTY_TEXT,
  MONTH_FILE_STATE,
  foldBeforeStartRuns,
  isFailingMonth,
  monthFromNote,
  monthRowNote,
  monthlyFilesHelp,
} from '../../constants/busyFile';
import { getMonthlyFiles, getReportArchiveDownloadUrl } from '../../services/api/monthlyReportsApi';
import { formatAddedDate, formatArchiveSize, formatPeriodLabel } from '../../utils/reportArchive';
import BooksStatusBlock from './BooksStatusBlock';

// The archive "kind" of the Busy monthly file (the other kinds, opd-otc-lab and
// ipd, are never listed here — OR-14).
const BUSY_KIND = 'busy';
const REPORT_NOT_AVAILABLE = 'REPORT_NOT_AVAILABLE';
// A row whose Download 404'd: CHECKING while the list is re-read, MISSING if that
// re-read failed (the list is stale, so the row stays "not found"). A successful
// re-read clears the flag — the fresh list decides the row.
const NOT_FOUND = Object.freeze({ CHECKING: 'CHECKING', MISSING: 'MISSING' });

const CARD_TITLE = 'Monthly files';
const CARD_SUBTITLE = 'All months · whole organisation';
const HELP_LABEL = 'About Monthly files';
const GUIDE_LINK_TEXT = 'How to import into Busy';
const INSIDE_TOGGLE_TEXT = 'What’s inside ▾';
const INSIDE_FOOTER = `${BUSY_SERIES_RULE} Row 1 is the header; entries start on row 2. Empty sheets keep their header row.`;
const AVAILABLE_COPY = 'Available for download';

// "What's inside": the 10 series sheets as (receipts, refunds) pairs, then the
// read-only Summary and Checks sheets.
// Both come from the one shared constant (constants/busyFile.js), in file order.
const SHEET_PAIRS = BUSY_SERIES.reduce((pairs, series, i) => {
  if (i % 2 === 0) pairs.push([series.sheet]);
  else pairs[pairs.length - 1].push(series.sheet);
  return pairs;
}, []);

const SECONDARY_BTN = 'rounded-lg border border-slate-400 bg-white px-3 py-1.5 text-[0.8125rem] font-semibold text-teal-800 hover:bg-slate-100 dark:border-slate-500 dark:bg-slate-800 dark:text-teal-300 dark:hover:bg-slate-700';
const TEAL_PANEL = 'rounded-lg border border-teal-400 bg-teal-50 text-teal-900 dark:border-teal-700 dark:bg-teal-900/40 dark:text-teal-100';

// Row tones: ready (white), failing month (amber — the ONE month that is
// actually failing), everything else that is merely waiting (grey, dashed).
const ROW_TONE = {
  ready: 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800',
  amber: 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/40',
  grey: 'border-dashed border-slate-400 bg-slate-100 dark:border-slate-600 dark:bg-slate-950',
};
const TITLE_TONE = {
  ready: 'text-slate-900 dark:text-slate-50',
  amber: 'text-amber-900 dark:text-amber-100',
  grey: 'text-slate-700 dark:text-slate-300',
};
const NOTE_TONE = {
  ready: 'text-slate-600 dark:text-slate-300',
  amber: 'italic text-amber-900 dark:text-amber-100',
  grey: 'italic text-slate-700 dark:text-slate-300',
};
// Wide: one compact line per month (12 months stay short); narrow: stacked card.
const ROW_BASE = 'mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border px-3 py-2.5 last:mb-0 md:py-2';
const ROW_MAIN = 'min-w-0 basis-full md:flex md:flex-1 md:basis-0 md:items-baseline md:gap-4';
const ROW_TITLE = 'text-sm font-semibold md:w-[16.5rem] md:shrink-0';
const ROW_NOTE = 'mt-0.5 text-xs md:mt-0';

/**
 * Navigates to a presigned S3 URL via a programmatic, non-rendered anchor
 * (C6) — never `fetch()`/XHR (would require bucket CORS for zero gain, and
 * would pull a multi-MB PHI workbook through the JS heap). `Content-
 * Disposition: attachment` is set server-side, so this navigation IS the
 * download. The url is a local variable only — never React state — so it
 * is never rendered/copyable and is gone the instant this returns.
 */
function navigateToPresignedUrl(url) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function Spinner() {
  return (
    <svg aria-hidden="true" className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function MonthRow({ month, books, rowState, notFound, onDownload }) {
  const { period, file } = month;
  const available = month.state === MONTH_FILE_STATE.AVAILABLE && !!file && !notFound;
  // A not-found row never says "Available for download"; the neutral line shows
  // while the list is re-read, and nothing after a failed re-read (the red
  // message below says it).
  const note = notFound ? (notFound === NOT_FOUND.CHECKING ? FILE_RECHECKING_TEXT : null) : monthRowNote(month, books);
  const tone = available ? 'ready' : (isFailingMonth(month) ? 'amber' : 'grey');
  const title = `Busy import · ${formatPeriodLabel(period)}`;
  const downloading = !!rowState?.downloading;
  const fromNote = available ? monthFromNote(month, books) : null;

  return (
    <div data-testid={`monthly-file-row-${period}`} data-tone={tone} className={`${ROW_BASE} ${ROW_TONE[tone]}`}>
      <div className={ROW_MAIN}>
        <div className={`${ROW_TITLE} ${TITLE_TONE[tone]}`}>{title}</div>
        {available ? (
          <div className={`${ROW_NOTE} ${NOTE_TONE[tone]}`}>
            {`${formatArchiveSize(file.size_bytes)} · Added ${formatAddedDate(file.uploaded_at)} · `}
            {fromNote && <span data-testid={`monthly-file-from-${period}`}>{`${fromNote} · `}</span>}
            <span data-testid={`monthly-file-name-${period}`} className="break-all font-mono text-[11px] text-slate-600 dark:text-slate-300">
              {file.filename}
            </span>
          </div>
        ) : note && (
          <div data-testid={`monthly-file-note-${period}`} className={`${ROW_NOTE} ${NOTE_TONE[tone]}`}>
            {note}
          </div>
        )}
      </div>

      {available && (
        <div className="flex basis-full items-center justify-between gap-3 md:basis-auto md:justify-end">
          <span className="text-xs text-slate-600 dark:text-slate-300">{AVAILABLE_COPY}</span>
          <button
            type="button"
            data-testid={`monthly-file-download-${period}`}
            aria-label={`Download ${title}`}
            disabled={downloading}
            onClick={() => onDownload(period)}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-teal-600 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-500 dark:disabled:border-slate-700 dark:disabled:bg-slate-800 dark:disabled:text-slate-400"
          >
            {downloading && <Spinner />}
            {downloading ? 'Preparing…' : 'Download'}
          </button>
        </div>
      )}

      {rowState?.error && (
        <p role="alert" data-testid={`monthly-file-error-${period}`} className="basis-full text-xs text-rose-700 dark:text-rose-300 md:text-right">
          {rowState.error}
        </p>
      )}
    </div>
  );
}

// A run of 2+ months before the books started: one line, not one row each.
function FoldedRow({ item, books }) {
  const newest = item.folded[0];
  return (
    <div data-testid={`monthly-file-fold-${newest.period}`} data-tone="grey" className={`${ROW_BASE} ${ROW_TONE.grey} md:py-1.5`}>
      <div className={ROW_MAIN}>
        <div data-testid={`monthly-file-foldtitle-${newest.period}`} className={`${ROW_TITLE} ${TITLE_TONE.grey}`}>{`Busy import · ${item.label}`}</div>
        <span className="sr-only"> · </span>
        <div data-testid={`monthly-file-foldnote-${newest.period}`} className={`${ROW_NOTE} ${NOTE_TONE.grey}`}>{monthRowNote(newest, books)}</div>
      </div>
    </div>
  );
}

function FiscalYearGroup({ fy, open, onToggle, children }) {
  const bodyId = `monthly-files-fy-${fy.fy}`;
  const monthWord = fy.month_count === 1 ? 'month' : 'months';
  return (
    <div data-testid={`monthly-files-fy-${fy.fy}`} className="mb-2.5 last:mb-0">
      <div className="mb-2 flex flex-wrap items-center gap-2.5 border-b border-slate-300 pb-1.5 dark:border-slate-600">
        <button
          type="button"
          data-testid={`monthly-files-fy-toggle-${fy.fy}`}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => onToggle(fy.fy, open)}
          className="rounded-lg border border-slate-400 bg-white px-2.5 py-1 text-[0.8125rem] font-bold text-slate-900 hover:bg-slate-100 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-50 dark:hover:bg-slate-700"
        >
          <span aria-hidden="true" className="mr-1 inline-block w-3 text-teal-700 dark:text-teal-300">{open ? '▾' : '▸'}</span>
          {fy.label}
        </button>
        <span className="text-xs text-slate-600 dark:text-slate-300">{`${fy.month_count} ${monthWord} · ${fy.available_count} ready`}</span>
      </div>
      {open && <div id={bodyId}>{children}</div>}
    </div>
  );
}

/**
 * ReportArchiveCard — the "Monthly files" section of Reports & Accounts
 * (BUSY-VOUCHER R3 FE-CARD3; R3-S4/S5/S6; OR-14/28/33/77/78). Self-contained:
 * owns its permission gate (DOWNLOAD_BUSY_FILES = ACCOUNTANT + ADMIN, OR-84)
 * and its one API read. It is ORG-WIDE — it takes no props, so the page's
 * Month / Doctor pickers can never reach it.
 *
 * - Mount: exactly ONE `getMonthlyFiles()` (ref-guarded; the api call is
 *   single-flight too). Never re-fetched on re-render.
 * - Every row's state, "made on" date, attempts and held-behind month are
 *   decided by the SERVER (`fiscal_years[].months[]`); this file only words
 *   them (constants/busyFile.js) — no browser date maths (IST/UTC trap).
 * - Newest FY expanded, older FYs collapsed; the user's toggles are local.
 * - Download = presign `busy` -> `navigateToPresignedUrl`. A 404 flips that row
 *   and re-reads the list ONCE (user-triggered, never a loop). A refused
 *   download record (OR-78) arrives as the api layer's "Downloads are
 *   temporarily unavailable. Try again." and shows on that row only. While the
 *   re-read is in flight the row shows a neutral line (never "Available for
 *   download"); the message says "list has been refreshed" only if it worked.
 * - No ticks, zip or PIN (R4).
 * - The guide link is a plain anchor, not react-router's <Link>: the page's
 *   many test suites import this card without a router mock, and jest (CRA 5)
 *   cannot resolve react-router-dom v7 — a <Link> import here would break every
 *   one of them at import time. Swap to <Link> if the SPA-navigation matters
 *   more than that (the page tests then need the routerMock).
 */
export default function ReportArchiveCard() {
  const { hasPermission } = usePermissions();
  const canDownload = hasPermission(BUSY_FILES_PERMISSIONS.DOWNLOAD_BUSY_FILES);

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [rowStates, setRowStates] = useState({});
  const [notFound, setNotFound] = useState({});
  const [fyOpen, setFyOpen] = useState({});
  const [showInside, setShowInside] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const fetchedRef = useRef(false);
  const downloadingRef = useRef(new Set());

  const loadFiles = useCallback(async () => {
    const res = await getMonthlyFiles();
    if (res.success) {
      setData(res.data);
      setLoadError(null);
      setNotFound({});
    } else {
      setLoadError(res.error);
    }
    return res.success;
  }, []);

  // Mount: exactly one fetch, gated on the permission being held.
  useEffect(() => {
    if (!canDownload || fetchedRef.current) return;
    fetchedRef.current = true;
    loadFiles();
  }, [canDownload, loadFiles]);

  const handleDownload = useCallback(async (period) => {
    if (downloadingRef.current.has(period)) return;
    downloadingRef.current.add(period);
    setRowStates((prev) => ({ ...prev, [period]: { downloading: true, error: null } }));

    const res = await getReportArchiveDownloadUrl(period, BUSY_KIND);
    downloadingRef.current.delete(period);
    if (res.success) {
      navigateToPresignedUrl(res.data.url);
      setRowStates((prev) => ({ ...prev, [period]: { downloading: false, error: null } }));
      return;
    }
    if (res.code === REPORT_NOT_AVAILABLE) {
      // Re-read the list ONCE (user-triggered). Say "refreshed" only if that worked.
      setRowStates((prev) => ({ ...prev, [period]: { downloading: false, error: null } }));
      setNotFound((prev) => ({ ...prev, [period]: NOT_FOUND.CHECKING }));
      const refreshed = await loadFiles();
      if (!refreshed) setNotFound((prev) => ({ ...prev, [period]: NOT_FOUND.MISSING }));
      setRowStates((prev) => ({
        ...prev,
        [period]: { downloading: false, error: refreshed ? FILE_NOT_FOUND_REFRESHED_TEXT : FILE_NOT_FOUND_RETRY_TEXT },
      }));
      return;
    }
    setRowStates((prev) => ({ ...prev, [period]: { downloading: false, error: res.error } }));
  }, [loadFiles]);

  const toggleFy = useCallback((fy, open) => setFyOpen((prev) => ({ ...prev, [fy]: !open })), []);

  if (!canDownload) return null;

  const fiscalYears = data?.fiscal_years || [];
  // Never switched on (no months at all): the status block already says "To
  // start generating monthly files, contact ArogyaKhata support." — say it once.
  const switchedOff = !!data?.books && !data.books.enabled;

  return (
    <section
      data-testid="monthly-files-card"
      className="mb-5 rounded-xl border border-t-[3px] border-slate-300 border-t-teal-600 bg-white p-4 dark:border-slate-600 dark:border-t-teal-600 dark:bg-slate-900"
    >
      <div className="mb-2.5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-50">{CARD_TITLE}</h2>
            <button
              type="button"
              data-testid="monthly-files-help-toggle"
              aria-label={HELP_LABEL}
              aria-expanded={showHelp}
              aria-controls="monthly-files-help"
              onClick={() => setShowHelp((prev) => !prev)}
              className="rounded-md border border-slate-400 bg-white px-1.5 text-[0.9rem] font-semibold leading-snug text-teal-800 hover:bg-slate-100 dark:border-slate-500 dark:bg-slate-800 dark:text-teal-300 dark:hover:bg-slate-700"
            >
              &#9432;
            </button>
          </div>
          <div className="mt-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">{CARD_SUBTITLE}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="monthly-files-inside-toggle"
            aria-expanded={showInside}
            aria-controls="monthly-files-inside"
            onClick={() => setShowInside((prev) => !prev)}
            className={SECONDARY_BTN}
          >
            {INSIDE_TOGGLE_TEXT}
          </button>
          <a href={ROUTES.BUSY_IMPORT_GUIDE} data-testid="monthly-files-guide-link" className={SECONDARY_BTN}>
            {GUIDE_LINK_TEXT}
          </a>
        </div>
      </div>

      {showHelp && (
        <div id="monthly-files-help" data-testid="monthly-files-help" className={`mb-2.5 px-3 py-2 text-[0.8125rem] ${TEAL_PANEL}`}>
          {monthlyFilesHelp(data?.release_day)}
        </div>
      )}

      {showInside && (
        <div id="monthly-files-inside" data-testid="monthly-files-inside" className={`mb-2.5 px-3 py-2.5 ${TEAL_PANEL}`}>
          <div className="mb-1.5 text-[0.8125rem] font-bold">{`Each month's file has ${BUSY_SHEETS.length} sheets`}</div>
          <table className="w-full border-collapse text-[0.8125rem]">
            <thead>
              <tr>
                <th className="border-b border-teal-400 px-2 py-1 text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-teal-800 dark:border-teal-700 dark:text-teal-200">Receipts sheet</th>
                <th className="border-b border-teal-400 px-2 py-1 text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-teal-800 dark:border-teal-700 dark:text-teal-200">Refunds sheet</th>
              </tr>
            </thead>
            <tbody>
              {SHEET_PAIRS.map(([receipts, refunds]) => (
                <tr key={receipts}>
                  <td data-testid="monthly-files-sheet" className="border-b border-teal-400 px-2 py-1 dark:border-teal-700">{receipts}</td>
                  <td data-testid="monthly-files-sheet" className="border-b border-teal-400 px-2 py-1 dark:border-teal-700">{refunds}</td>
                </tr>
              ))}
              {BUSY_READ_ONLY_SHEETS.map(({ sheet, note }, index) => (
                <tr key={sheet}>
                  <td
                    data-testid="monthly-files-sheet"
                    colSpan={2}
                    className={`px-2 py-1 ${index < BUSY_READ_ONLY_SHEETS.length - 1 ? 'border-b border-teal-400 dark:border-teal-700' : ''}`}
                  >
                    <b>{sheet}</b> {`— ${note}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1.5 text-xs text-teal-800 dark:text-teal-200">{INSIDE_FOOTER}</p>
        </div>
      )}

      {data === null && !loadError && (
        <div data-testid="monthly-files-loading" className="space-y-2">
          <div className="h-5 w-48 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
          <div className="h-12 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-800" />
          <div className="h-12 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-800" />
        </div>
      )}

      {data === null && loadError && (
        <div role="alert" data-testid="monthly-files-load-error" className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100">
          {loadError}
        </div>
      )}

      {data !== null && (
        <>
          <BooksStatusBlock books={data.books} booksStatusError={data.books_status_error} />

          {fiscalYears.length === 0 && !switchedOff && (
            <div data-testid="monthly-files-empty" className="rounded-lg border border-slate-300 bg-slate-100 p-4 text-center text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
              {MONTHLY_FILES_EMPTY_TEXT}
            </div>
          )}

          {fiscalYears.map((fy, index) => (
            <FiscalYearGroup
              key={fy.fy}
              fy={fy}
              open={fyOpen[fy.fy] ?? (index === 0)}
              onToggle={toggleFy}
            >
              {foldBeforeStartRuns(fy.months, data.books).map((item) => (item.folded ? (
                <FoldedRow key={item.folded[0].period} item={item} books={data.books} />
              ) : (
                <MonthRow
                  key={item.month.period}
                  month={item.month}
                  books={data.books}
                  rowState={rowStates[item.month.period]}
                  notFound={notFound[item.month.period]}
                  onDownload={handleDownload}
                />
              )))}
            </FiscalYearGroup>
          ))}

          {fiscalYears.length > 0 && (
            <p data-testid="monthly-files-trademark-note" className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              {BUSY_TRADEMARK_NOTE}
            </p>
          )}
        </>
      )}
    </section>
  );
}
